import React from 'react';
import { FiImage, FiTarget, FiGlobe, FiMaximize, FiInfo } from 'react-icons/fi';
import { HiSparkles, HiPaintBrush } from 'react-icons/hi2';
import './Preview.css';

/**
 * Preview Component
 * Shows a preview of selected options and processed image
 */
const Preview = ({ selectedImage, backgroundType, backgroundColor, resultImageUrl, passportSize, customWidth, customHeight, selectedCountry }) => {
  if (!selectedImage) {
    return (
      <div className="text-center p-8">
        <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-slate-800 dark:to-slate-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md">
          <FiImage className="text-4xl text-gray-400 dark:text-gray-500" />
        </div>
        <p className="text-gray-500 dark:text-gray-400 font-bold">Upload an image to see preview</p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">Drag & drop or click to upload</p>
      </div>
    );
  }

  // Show output image if available, otherwise show uploaded image
  const displayImageSrc = resultImageUrl || URL.createObjectURL(selectedImage);
  const isOutputImage = !!resultImageUrl;

  return (
    <div className="w-full flex flex-col md:flex-row gap-4 md:gap-6 p-2 md:p-4">
      {/* Image Preview */}
      <div className="flex-1 min-h-[200px] sm:min-h-[220px] flex items-center justify-center bg-gray-100 dark:bg-slate-900 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 relative group">
        <img
          src={displayImageSrc}
          alt={isOutputImage ? "Processed Preview" : "Preview"}
          className="max-w-full max-h-[200px] sm:max-h-[280px] object-contain shadow-2xl transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {isOutputImage && (
          <div className="absolute top-2 right-2 sm:top-4 sm:right-4 px-2 py-1 sm:px-3 sm:py-1 bg-green-500 text-white text-xs font-bold rounded-full shadow-lg">
            PROCESSED
          </div>
        )}
      </div>

      {/* Preview Details */}
      <div className="w-full md:w-64 space-y-3 md:space-y-4">
        <div className="p-4 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-100 dark:border-gray-700">
          <h4 className="text-xs font-bold text-gray-400 uppercase tracking-tighter mb-3">Live Configuration</h4>

          <div className="space-y-3">
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase">Background</p>
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                {backgroundType === 'transparent' ? (
                  <>
                    <HiSparkles className="text-blue-500" /> Transparent
                  </>
                ) : (
                  <>
                    <HiPaintBrush className="text-blue-500" /> Solid Color
                  </>
                )}
              </div>
            </div>

            {(passportSize || selectedCountry || (customWidth && customHeight)) && (
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase">Dimensions</p>
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                  {passportSize && (
                    <>
                      <FiTarget className="text-blue-500" /> 600x600 px
                    </>
                  )}
                  {selectedCountry && (
                    <>
                      <FiGlobe className="text-blue-500" /> {selectedCountry}
                    </>
                  )}
                  {customWidth && customHeight && !passportSize && !selectedCountry && (
                    <>
                      <FiMaximize className="text-blue-500" /> {customWidth}x{customHeight} px
                    </>
                  )}
                </div>
              </div>
            )}

            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase">Input File</p>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 truncate" title={selectedImage.name}>
                {selectedImage.name}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-900/30">
          <p className="text-xs text-blue-700 dark:text-blue-300 flex gap-2">
            <FiInfo className="mt-0.5 flex-shrink-0" />
            <span><span className="font-bold">Tip:</span> AI processing works best on portraits with clear subjects.</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Preview;


